import React, { useState, useEffect } from "react";
import { customToast } from "../../utility";
import "@gooddata/sdk-ui-filters/styles/css/main.css";
import { DateFilter } from "@gooddata/sdk-ui-filters";

import {
  DATE_FILTER_MODE,
  DEFAULT_DATE_FORMAT,
  availableGranularities,
  localIdentifierConstants,
  defaultDateFilterOptions,
  dateFilterOptionConstants,
  dateFilterGranularityConstants,
} from "./dateFilterConstants";
import { ENV } from "../../../config/env";
import { emptyString, errorMessages, toastTypes } from "../../../constants";
import { setItemToStorage } from "../../../system";

const {
  TODAY,
  THIS_YEAR,
  THIS_MONTH,
  THIS_QUARTER,
  LAST_QUARTER,
  LAST_12_MONTHS,
  LAST_4_QUARTERS,
} = localIdentifierConstants;
const { DATE, MONTH, QUARTER, YEAR } = dateFilterGranularityConstants;
const { ALL_TIME, ABSOLUTE_FROM, RELATIVE_PRESET } = dateFilterOptionConstants;

const defaultDateState = {
  selectedFilterOption: {
    to: 0,
    from: -6,
    name: emptyString,
    visible: true,
    granularity: DATE,
    type: RELATIVE_PRESET,
    localIdentifier: localIdentifierConstants.LAST_7_DAYS,
  },
  excludeCurrentPeriod: false,
};

const CustomDateFilter = ({
  dateState,
  setDateState,
  className = emptyString,
  isTodayExcluded = false,
}) => {
  const [state, setState] = useState(defaultDateState);

  const onApply = (selectedFilterOption, excludeCurrentPeriod) => {
    const isAbsoluteForm =
      selectedFilterOption.localIdentifier ===
      localIdentifierConstants.ABSOLUTE_FORM;
    const dataToSend = {
      ...conversionSelectedDate(selectedFilterOption),
      selectedFilterOption,
      excludeCurrentPeriod,
    };
    const updatedObj = { selectedFilterOption, excludeCurrentPeriod };

    if (isAbsoluteForm && isTodayExcluded) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const fromDate = new Date(selectedFilterOption.from);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = new Date(selectedFilterOption.to);
      toDate.setHours(0, 0, 0, 0);

      if (toDate < today && fromDate < today) {
        handleSubmit(updatedObj, dataToSend);
      } else {
        customToast(errorMessages.SELECT_PREVIOUS_DATE, toastTypes.ERROR);
      }
    } else {
      handleSubmit(updatedObj, dataToSend);
    }
  };

  const onCancel = () => {
    const dataToSend = {
      ...conversionSelectedDate(
        defaultDateFilterOptions.relativePreset["GDC.time.date"][2]
      ),
      ...defaultDateState,
    };
    const updatedObj = { ...defaultDateState };
    handleSubmit(updatedObj, dataToSend);
  };

  const handleSubmit = (updatedState, updatedData) => {
    setState(updatedState);
    setDateState(updatedData);
    setItemToStorage(
      ENV.DATE_PREFERENCE,
      JSON.stringify(updatedData)
    );
  };
  const getDateFilterOptions = (isTodayExcluded) => {
    const modifiedOptions = JSON.parse(
      JSON.stringify(defaultDateFilterOptions)
    ); // Deep clone the options

    if (isTodayExcluded) {
      // Remove "Today" from "GDC.time.date" in relativePreset
      modifiedOptions.relativePreset[dateFilterGranularityConstants.DATE] =
        modifiedOptions.relativePreset[
          dateFilterGranularityConstants.DATE
        ].filter((option) => option.localIdentifier !== TODAY);
    }

    return modifiedOptions;
  };

  //Get the current date
  const currentDate = new Date();
  //Get the current month
  const currentMonth = currentDate.getMonth();
  //Get the current year
  const currentYear = currentDate.getFullYear();
  //Get the current quarter
  const currentQuarter = Math.floor(currentDate.getMonth() / 3) * 3;
  //Calculate the start date for the current quarter
  const currentQuarterStart = new Date(currentYear, currentQuarter, 1);

  const conversionSelectedDate = (selectedDate) => {
    const { type, to, from, granularity, localIdentifier } = selectedDate;

    let startDate;
    let endDate;

    switch (type) {
      case ALL_TIME: {
        ({ startDate, endDate } = getAllTimeDateRange());
        break;
      }
      case ABSOLUTE_FROM: {
        ({ startDate, endDate } = getAbsoluteFromDateRange(from, to));
        break;
      }
      case RELATIVE_PRESET: {
        ({ startDate, endDate } = getDateRangeForGranularity(
          granularity,
          from,
          to,
          localIdentifier
        ));
        break;
      }
      default:
        //Handle other cases for type if needed
        break;
    }
    return { startDate, endDate };
  };

  const getAllTimeDateRange = () => {
    const startDate = new Date("2023-01-01T00:00:00.000Z");
    let endDate;
    if (isTodayExcluded) {
      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1); // Exclude today
    } else {
      endDate = currentDate;
    }
    endDate.setHours(23, 59, 59, 999); // Set to end of the day
    return { startDate, endDate };
  };

  const getAbsoluteFromDateRange = (from, to) => {
    let startDate = new Date(from);
    startDate.setHours(0, 0, 0, 0);
    let endDate = new Date(to);
    endDate.setHours(23, 59, 59, 999);
    return { startDate, endDate };
  };

  const getDateRangeForGranularity = (
    granularity,
    from,
    to,
    localIdentifier
  ) => {
    let startDate, endDate;

    switch (granularity) {
      case YEAR: {
        ({ startDate, endDate } = getYearDateRange(from, to, localIdentifier));
        break;
      }
      case MONTH: {
        ({ startDate, endDate } = getMonthDateRange(from, to, localIdentifier));
        break;
      }
      case DATE: {
        ({ startDate, endDate } = getDateRange(from, to));
        break;
      }
      case QUARTER: {
        ({ startDate, endDate } =
          getDateRangeForLocalIdentifier(localIdentifier));
        break;
      }
      default:
        //Handle other cases for granularity if needed
        break;
    }
    return { startDate, endDate };
  };

  const getYearDateRange = (from, to, localIdentifier) => {
    let startDate = new Date(currentYear + from, 0, 1); // January 1st
    let endDate;

    startDate.setHours(0, 0, 0, 0);

    if (isTodayExcluded && localIdentifier === THIS_YEAR) {
      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
    } else {
      endDate = new Date(currentYear + to, 11, 31); // December 31st
    }
    endDate.setHours(23, 59, 59, 999);

    return { startDate, endDate };
  };

  const getMonthDateRange = (from, to, localIdentifier) => {
    //Calculate the "fromDate" based on value.from
    let startDate = new Date(currentYear, currentMonth + from, 1);
    let endDate;

    startDate.setHours(0, 0, 0, 0);

    const condition =
      isTodayExcluded &&
      (localIdentifier === THIS_MONTH || localIdentifier === LAST_12_MONTHS);

    if (condition) {
      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
    } else {
      //Calculate the "toDate" based on value.to
      const toMonth = currentMonth + to + 1;
      const lastDateOfMonth = new Date(currentYear, toMonth, 0).getDate();
      endDate = new Date(currentYear, toMonth - 1, lastDateOfMonth);
    }
    endDate.setHours(23, 59, 59, 999);

    return { startDate, endDate };
  };

  const getDateRange = (from, to) => {
    //Calculate the date range for the last 7 days, last 30 days, last 90 days
    let startDate = new Date();
    startDate.setDate(currentDate.getDate() + from);
    startDate.setHours(0, 0, 0, 0);

    let endDate = new Date();
    endDate.setDate(currentDate.getDate() + to);
    endDate.setHours(23, 59, 59, 999);

    // Check if endDate is today and change it to yesterday
    const condition = endDate > new Date() && isTodayExcluded;

    if (condition) {
      startDate = new Date();
      startDate.setDate(currentDate.getDate() + from - 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
      endDate.setHours(23, 59, 59, 999);
    }

    return { startDate, endDate };
  };

  const getDateRangeForLocalIdentifier = (localIdentifier) => {
    let startDate, endDate;

    switch (localIdentifier) {
      case THIS_QUARTER: {
        ({ startDate, endDate } = getThisQuaterDateRange(localIdentifier));
        break;
      }
      case LAST_QUARTER: {
        ({ startDate, endDate } = getLastQuaterDateRange());
        break;
      }
      case LAST_4_QUARTERS: {
        ({ startDate, endDate } = getLastFourQuaterDateRange(localIdentifier));
        break;
      }
      default:
        //Handle other cases for localIdentifier if needed
        break;
    }

    return { startDate, endDate };
  };

  const getThisQuaterDateRange = (localIdentifier) => {
    //Calculate the start and end dates for the current quarter
    let startDate = currentQuarterStart;
    let endDate;

    startDate.setHours(0, 0, 0, 0);

    const condition = isTodayExcluded && localIdentifier === THIS_QUARTER;

    if (condition) {
      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
    } else {
      endDate = new Date(currentYear, currentQuarter + 3, 0);
    }
    endDate.setHours(23, 59, 59, 999);

    return { startDate, endDate };
  };

  const getLastQuaterDateRange = () => {
    //Calculate the start and end dates for the last quarter
    let startDate = new Date(currentYear, currentQuarter - 3, 1);
    startDate.setHours(0, 0, 0, 0);

    let endDate = new Date(currentYear, currentQuarter, 0);
    endDate.setHours(23, 59, 59, 999);

    return { startDate, endDate };
  };

  const getLastFourQuaterDateRange = (localIdentifier) => {
    //Calculate the start and end dates for the last 4 quarters
    let quarters = Array.from({ length: 4 }, (_, i) => {
      const start = new Date(currentQuarterStart);
      start.setMonth(start.getMonth() - i * 3);
      start.setHours(0, 0, 0, 0);

      let end;
      if (i === 0 && isTodayExcluded && localIdentifier === LAST_4_QUARTERS) {
        end = new Date();
        end.setDate(end.getDate() - 1);
      } else {
        end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
      }
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }).reverse(); //Reverse the order to have the most recent quarter first
    const startDate = quarters[0].start;
    const endDate = quarters[3].end;

    return { startDate, endDate };
  };

  const onOpen = () => {
    const [container] = document.getElementsByClassName("main-container");
    if (container) {
      container.style.setProperty("overflow-y", "hidden", "important");
    }
  };

  const onClose = () => {
    const [container] = document.getElementsByClassName("main-container");
    if (container) {
      container.removeAttribute("style");
    }
  };

  useEffect(() => {
    if (dateState?.selectedFilterOption) {
      const { excludeCurrentPeriod, selectedFilterOption } = dateState;
      setState({ selectedFilterOption, excludeCurrentPeriod });
    }
  }, [dateState]);

  useEffect(() => {
    if (isTodayExcluded) {
      setState(defaultDateState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`good-data-date-filter ${className}`}
      aria-label="date-filter"
    >
      <DateFilter
        onOpen={onOpen}
        onClose={onClose}
        onApply={onApply}
        onCancel={onCancel}
        customFilterName="Selected date"
        dateFormat={DEFAULT_DATE_FORMAT}
        dateFilterMode={DATE_FILTER_MODE}
        filterOptions={getDateFilterOptions(isTodayExcluded)}
        availableGranularities={availableGranularities}
        excludeCurrentPeriod={state.excludeCurrentPeriod}
        selectedFilterOption={state.selectedFilterOption}
      />
    </div>
  );
};

export default CustomDateFilter;
